import React, {
  ComponentType,
  useEffect,
  useState
} from 'react'
import { connect } from 'react-redux'
import {
  Container,
  Grid,
  Icon,
  Modal,
  Popup,
  Segment
} from 'semantic-ui-react'
import concat from 'lodash/concat'
import {
  addDays,
  addMinutes,
  format,
  isAfter,
  isBefore,
  startOfDay,
  subMinutes
} from 'date-fns'
// Local
import { formatDob } from '../../common/formatUtils'
import {
  API_URL,
  ENCOUNTER,
  FORMAT,
  LIST_MAX_PAGES,
  LIST_PAGE_SIZE,
  PATIENT,
  REVIEW
} from '../../../constant'
import { api } from '../../../action/api'
import { searchGenerator } from '../../../action/search'
import { updateUserWorklistFilters } from '../../../action/user'
import { DatePickerIcon } from '../../common/datePickerIcon'
import { patientMrn } from '../../common/patientUtils'
import PdfHuddleReport from '../../common/pdfHuddleReport'
import PractitionerSelect from '../../common/practitionerSelect'
import SubHeader from '../../common/SubHeader'
import Table from '../../common/Table'
import { semiColonsToLineBreaks } from '../../v2/utils/utils'
import { User } from '../../authenticated/types'
import { RootState } from '../../../store'
// CSS
import './clinicalAdminWorklist.css'
import If from '../../common/if'

const SafeTable = Table as unknown as ComponentType<any>

const SafeSegment = Segment as unknown as ComponentType<any>
const SafeGrid = Grid as unknown as ComponentType<any>
const SafeModal = Modal as unknown as ComponentType<any>

type WorklistFilters = {
  start?: Date | null
  view?: string | null
  defaultPatientRegion?: string
  defaultPatientOrgId?: string
}

type CurrentUser = User & {
  userRole: string
  worklistFilters: WorklistFilters
}

type Patient = {
  tenantId: string
  firstName: string
  lastName: string
  birthDate: string
}

type Practitioner = {
  firstName: string
  lastName: string
}

type EncounterRecord = {
  id: string
  start: string
  patient: Patient
  practitioner: Practitioner
}

type QueryFilters = {
  start?: Date
  patientStatus?: string
  reviewCode?: string[]
  encounterStatus?: string[]
  practitionerId?: string | null
  patientRegion?: string
  patientOrgId?: string
}

type ClinicalAdminWorklistOwnProps = {
  onSelect?: (
    id: string,
    event: React.MouseEvent
  ) => void
}

type ClinicalAdminWorklistReduxProps = {
  currentUser: CurrentUser
  updateUserWorklistFilters: (
    user: CurrentUser,
    filters: WorklistFilters
  ) => void
}

type ClinicalAdminWorklistProps = ClinicalAdminWorklistOwnProps & ClinicalAdminWorklistReduxProps

/**
 * Provides a worklist view of encounters for the Clinical Admin user role
 *
 * @param onSelect Callback function to execute when an encounter is selected
 *        arguments: Encounter ID
 */
function ClinicalAdminWorklist(props: ClinicalAdminWorklistProps): JSX.Element {
  const {
    onSelect,
    currentUser,
    updateUserWorklistFilters: updateFilters
  } = props

  const userFilters = currentUser.worklistFilters
  const initialViewDate = userFilters.start
    ? startOfDay(new Date(userFilters.start))
    : startOfDay(new Date())
  const initialPractitionerId = userFilters.view
    ? userFilters.view
    : null

  const [
    viewDate,
    setViewDate
  ] = useState<Date>(initialViewDate)
  const [
    encounters,
    setEncounters
  ] = useState<EncounterRecord[]>([])
  const [
    practitionerId,
    setPractitionerId
  ] = useState<string | null>(initialPractitionerId)
  const [
    pagesDisplayed,
    setPagesDisplayed
  ] = useState<number>(0)
  const [ encounterSort ] = useState<string>('start asc')

  const fetchEncounters = (
    date: Date,
    practitioner: string | null,
    offset: number,
    currentEncounters: EncounterRecord[]
  ) => {
    const searchQuery: QueryFilters = {
      start: date,
      patientStatus: PATIENT.STATUS.ACTIVE,
      reviewCode: [
        REVIEW.CODE.READY_FOR_PHYSICIAN,
        REVIEW.CODE.READY_FOR_CODER
      ],
      encounterStatus: [
        ENCOUNTER.STATUS.ARRIVED,
        ENCOUNTER.STATUS.FINISHED,
        ENCOUNTER.STATUS.IN_PROGRESS,
        ENCOUNTER.STATUS.PLANNED
      ]
    }

    if (practitioner) {
      searchQuery.practitionerId = practitioner
    }

    if (!searchQuery.patientRegion) {
      const defaultRegions = currentUser.worklistFilters.defaultPatientRegion
      if (defaultRegions) {
        searchQuery.patientRegion = defaultRegions
      }
    }

    if (!searchQuery.patientOrgId) {
      const defaultOrgId = currentUser.worklistFilters.defaultPatientOrgId
      if (defaultOrgId) {
        searchQuery.patientOrgId = defaultOrgId
      }
    }

    const url = API_URL.ENCOUNTER_SEARCH_WITH_ROLE(currentUser.userRole)
    const limit = offset === 0
      ? LIST_PAGE_SIZE + 1
      : LIST_PAGE_SIZE
    const params = searchGenerator(
      searchQuery,
      limit,
      offset,
      encounterSort,
      undefined
    )

    api.get(
      url,
      { params }
    ).then(({ data }: { data: EncounterRecord[] }) => {
      const results = offset === 0
        ? data
        : concat(
          currentEncounters,
          data
        )
      const pages = offset === 0
        ? 1
        : pagesDisplayed + 1
      setViewDate(date)
      setEncounters(results)
      setPractitionerId(practitioner)
      setPagesDisplayed(pages)
    })
  }

  useEffect(() => {
    fetchEncounters(
      viewDate,
      practitionerId,
      0,
      []
    )
  }, [])

  const onPractitionerSelect = (
    event: React.SyntheticEvent,
    data: {
      name: string
      value: string | null
    }
  ) => {
    const newPractitionerId = data.value === '' || data.value === null
      ? null
      : data.value
    const filters = currentUser.worklistFilters
    filters.view = newPractitionerId
    updateFilters(
      currentUser,
      filters
    )
    fetchEncounters(
      viewDate,
      newPractitionerId,
      0,
      []
    )
  }

  const changeDay = (newDate: Date) => {
    const start = startOfDay(new Date(newDate))
    const filters = currentUser.worklistFilters
    filters.start = start
    updateFilters(
      currentUser,
      filters
    )
    fetchEncounters(
      start,
      practitionerId,
      0,
      []
    )
  }

  const incrementViewDate = () => {
    changeDay(addDays(viewDate, 1))
  }

  const decrementViewDate = () => {
    changeDay(addDays(viewDate, -1))
  }

  const onDateSelect = (value: Date) => {
    changeDay(value)
  }

  const onMore = () => {
    const offset = LIST_PAGE_SIZE * pagesDisplayed + 1
    fetchEncounters(
      viewDate,
      practitionerId,
      offset,
      encounters
    )
  }

  const selectRecord = (
    id: string,
    event: React.MouseEvent
  ) => {
    if (onSelect && id) {
      onSelect(
        id,
        event
      )
    }
  }

  const mobilePatientName = (patient: Patient): string => (
    `${patient.firstName} ${patient.lastName} ( ${formatDob(
      patient.birthDate,
      FORMAT.DATE
    )} ) `
  )

  const rowClassName = (startDate: string): string => {
    const baseClass = 'cursor-pointer'
    const isOnSchedule = viewDate.getTime() === startOfDay(new Date()).getTime()
    const encounterRange = 30

    if (!isOnSchedule) {
      return baseClass
    }

    const lowerLimit = subMinutes(
      new Date(),
      encounterRange
    )
    const upperLimit = addMinutes(
      new Date(),
      encounterRange
    )
    const startDateTime = new Date(startDate)

    if (isBefore(startDateTime, lowerLimit)) {
      return `${baseClass} past`
    }
    if (isAfter(startDateTime, upperLimit)) {
      return `${baseClass} future`
    }
    return `${baseClass} current`
  }

  const generateTableData = (list: EncounterRecord[]) => {
    if (!list || list.length === 0) return []
    return list.map(record => {
      const time = format(
        new Date(record.start),
        FORMAT.TIME
      )
      const name = `${record.patient.firstName} ${record.patient.lastName}`
      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      const mrn = patientMrn(record.patient as any)
      const nameDob = mobilePatientName(record.patient)
      const practitionerName = `${record.practitioner.firstName} ${record.practitioner.lastName}`
      return {
        id: record.id,
        time,
        name,
        dob: formatDob(
          record.patient.birthDate,
          FORMAT.DATE
        ),
        mrn: semiColonsToLineBreaks(mrn || ''),
        nameDob,
        practitioner: practitionerName,
        meta: {
          className: rowClassName(record.start)
        }
      }
    })
  }

  const dateTitle = (): string => {
    const today = startOfDay(new Date())
    const tomorrow = addDays(today, 1)
    const yesterday = addDays(today, -1)

    if (viewDate.getTime() === today.getTime()) {
      return 'Today\'s Patients'
    }
    if (viewDate.getTime() === tomorrow.getTime()) {
      return 'Tomorrow\'s Patients'
    }
    if (viewDate.getTime() === yesterday.getTime()) {
      return 'Yesterday\'s Patients'
    }
    if (viewDate.getTime() < new Date().getTime()) {
      return `Patients from ${format(viewDate, FORMAT.DAY_DATE)}`
    }
    return `Patients on ${format(viewDate, FORMAT.DAY_DATE)}`
  }

  const getLeftSubHeader = (): string => dateTitle()

  const getRightSubHeader = (): JSX.Element => {
    const encounterIds = encounters.map(item => item.id)
    const region = currentUser.worklistFilters.defaultPatientRegion
    const orgId = currentUser.worklistFilters.defaultPatientOrgId
    return (
      <SafeGrid>
        <Grid.Row style={ { paddingBottom: '0rem' } }>
          <Grid.Column
            floated="right"
            align="right"
          >
            <span onClick={ decrementViewDate }>
              <Popup
                content="Previous Day"
                mouseEnterDelay={ 1000 }
                trigger={
                  <Icon
                    name="arrow alternate circle left outline"
                    size="large"
                    color="grey"
                    className="pointer"
                  />
                }
              />
            </span>
            <span>
              <DatePickerIcon
                onSelect={ onDateSelect }
                popperClassName="datePickerIcon-popper-left-adjust"
              />
            </span>
            <span onClick={ incrementViewDate }>
              <Popup
                content="Next Day"
                mouseEnterDelay={ 1000 }
                trigger={
                  <Icon
                    name="arrow alternate circle right outline"
                    size="large"
                    color="grey"
                    className="pointer"
                  />
                }
              />
            </span>
            <If isTrue={ encounterIds.length === 0 }>
              <span>
                <Icon size="large" className="empty" />
              </span>
            </If>
            <If isTrue={ encounterIds.length > 0 }>
              <span>
                <Popup
                  content="Daily Huddle PDF"
                  mouseEnterDelay={ 1000 }
                  trigger={
                    <SafeModal
                      size="fullscreen"
                      trigger={
                        <Icon
                          name="print"
                          size="large"
                          color="grey"
                          className="pointer"
                        />
                      }
                    >
                      <Modal.Content>
                        { React.createElement(PdfHuddleReport as any, {
                          startDt: viewDate,
                          practitionerId,
                          region,
                          orgId
                        }) }
                      </Modal.Content>
                    </SafeModal>
                  }
                />
              </span>
            </If>
          </Grid.Column>
        </Grid.Row>
      </SafeGrid>
    )
  }

  const tableData = generateTableData(encounters)
  const orgId = currentUser.worklistFilters.defaultPatientOrgId

  let shouldDisplayMore = false
  let shouldDisplayMaxRecords = false

  if (tableData.length > pagesDisplayed * LIST_PAGE_SIZE) {
    shouldDisplayMore = pagesDisplayed < LIST_MAX_PAGES
    shouldDisplayMaxRecords = pagesDisplayed >= LIST_MAX_PAGES
    tableData.pop()
  }

  return (
    <div className="header-padding">
      <Container>
        <SubHeader
          left={ getLeftSubHeader() }
          right={ getRightSubHeader() }
        />
        <SafeSegment
          attached
          className="no-margin"
        >
          <SafeGrid>
            <Grid.Row>
              <Grid.Column
                floated="right"
                align="right"
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              >
                { React.createElement(PractitionerSelect as any, {
                  value: practitionerId,
                  allOption: true,
                  orgId,
                  onChange: onPractitionerSelect
                }) }
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column
                mobile={ 16 }
                tablet={ 16 }
                computer={ 16 }
              >
                <SafeTable
                  computerColumns={ [
                    {
                      name: 'Time',
                      value: 'time'
                    },
                    {
                      name: 'Name',
                      value: 'name'
                    },
                    {
                      name: 'DOB',
                      value: 'dob'
                    },
                    {
                      name: 'MRN(s)',
                      value: 'mrn'
                    },
                    {
                      name: 'Practitioner',
                      value: 'practitioner'
                    }
                  ] }
                  mobileColumns={ [
                    {
                      value: 'time',
                      width: '4'
                    },
                    {
                      value: 'nameDob',
                      width: '12'
                    }
                  ] }
                  data={ tableData }
                  onClick={ selectRecord }
                  more={ shouldDisplayMore }
                  max={ shouldDisplayMaxRecords }
                  onMore={ onMore }
                />
              </Grid.Column>
            </Grid.Row>
          </SafeGrid>
        </SafeSegment>
      </Container>
    </div>
  )
}

const ConnectedClinicalAdminWorklist = connect(
  (state: RootState) => ({
    currentUser: state.user
  }),
  { updateUserWorklistFilters } as any
)(ClinicalAdminWorklist as any)

export default ConnectedClinicalAdminWorklist as React.ComponentType<ClinicalAdminWorklistOwnProps>
